import { useLazyChangeApplicationStateQuery } from "@/modules/application/services";
import { setApplication } from "@/modules/application/state";
import { OperationNextState } from "@/shared/common/enums";
import { useAppDispatch, useAppSelector, useNotify } from "@/shared/hooks";
import { setActiveApplicationId } from "@/shared/state/common";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  refetch: () => void;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const DeleteApplicationModal = ({
  refetch,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const dispatch = useAppDispatch();
  const application = useAppSelector((state) => state.applications.application);

  const [changeState, { isLoading: isDeleteApplicationLoading }] = useLazyChangeApplicationStateQuery();

  const handleDeleteApplication = () => {
    if (application?.id) {
      changeState({
        id: application.id,
        data: {
          applicationType: application.applicationType,
          nextState: OperationNextState.Delete,
        },
      })
        .unwrap()
        .then(() => {
          setIsDeleteModalOpen(false);
          dispatch(setApplication({}));
          refetch();
          showSuccessSnackbar(t("alerts.applicationHasBeenSuccessfullyDeleted"));
        })
        .catch((err) => {
          showErrorSnackbar(err);
          setIsDeleteModalOpen(false);
          dispatch(setApplication({}));
        }).finally(() => {
        dispatch(setActiveApplicationId(null));
      });
    }
  };

  return (
    <Modal
      isOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      onSubmit={handleDeleteApplication}
      buttonProps={{
        confirm: {
          buttonText: t("general.delete"),
          type: "danger",
          isLoading: isDeleteApplicationLoading,
          disabled: isDeleteApplicationLoading,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
      titleProps={{
        title: t("application.deleteModal.title"),
        size: "large"
      }}
    >
      <div>
        <Text>{t("application.deleteModal.description")}</Text>
      </div>
    </Modal>
  );
};
